/**
 * A topic reference in skill-body text is a `/`-prefixed token naming a topic
 * the procedure investigates — e.g. `/imu/data` — optionally followed by a
 * dotted field path (`/imu/data.linear_acceleration.x`). The topic *name* is
 * what a skill stores in `relevant_topics`; the field suffix is only prose in
 * the body.
 *
 * Recognition is grammar-driven, not catalog-driven: a token is a reference
 * when its `/` sits at a boundary and it matches the topic grammar (a slash-
 * separated name, optionally followed by a dotted field path), whether or not
 * the name is already a known org topic. This lets an author reference a topic
 * for data that does not exist yet. The org catalog is advisory — it only
 * decides whether a resolved reference is styled as known or not-yet-known (see
 * {@link TopicReferenceMatch.known}), never whether it counts as a reference.
 */

// Trailing characters that are not valid in a topic name or field path, so
// they can be trimmed off a captured token. `.` is intentionally strippable
// too: a trailing `.` is sentence punctuation, while a `.` *between* path
// segments is preserved because it is not at the end. This trims markdown and
// prose wrappers a reference commonly sits inside — backtick (`` `/topic` ``),
// bold (`**/topic**`), parentheses, quotes, commas, periods.
const TRAILING_NON_TOPIC = /[^A-Za-z0-9_/-]+$/;

// Wrapper characters that may immediately precede a reference's leading `/`
// without breaking the token — markdown/prose delimiters, not word characters.
// Lets `` `/topic `` and `**/topic` be recognized while `src/utils` is not.
const LEADING_WRAPPERS = /[`'"*([{<]/;

/**
 * A topic reference token: a `/`-prefixed, slash-separated name (no dots),
 * optionally followed by a dotted field path. The character classes anchor the
 * match so it stops at prose punctuation — `/gps/fix,` yields `/gps/fix`, and
 * `/imu/data.linear.x` is one token that splits into name `/imu/data` and field
 * `.linear.x` at the first dot. Boundary eligibility (see
 * {@link startsAtTopicBoundary}) is checked separately by the caller.
 */
const TOPIC_REFERENCE = /\/[A-Za-z0-9_/-]+(?:\.[A-Za-z0-9_.-]+)?/g;

/**
 * True when the `/` at ``slashIndex`` may begin a topic reference: it starts the
 * string, follows whitespace, or follows a markdown/prose wrapper. This is the
 * same eligibility rule {@link activeTopicTokenAtCursor} applies, so extraction
 * and highlighting agree with autocomplete on the same text — an incidental
 * slash inside a word or path (`logs/imu/data`, `src/utils`, `http://…/x`) is
 * rejected rather than resolved to a topic.
 */
function startsAtTopicBoundary(body: string, slashIndex: number): boolean {
  if (slashIndex === 0) {
    return true;
  }
  const prev = body[slashIndex - 1];
  return /\s/.test(prev) || LEADING_WRAPPERS.test(prev);
}

/**
 * The `/topic[.field]` token the caret is currently inside, or `null` when the
 * caret is not within one. A token runs forward from a `/` to the caret; the
 * `/` must begin the current non-whitespace run or sit right after a markdown
 * wrapper (so a reference typed inside backticks or bold still autocompletes).
 */
export interface ActiveTopicToken {
  /** Offset of the leading `/`. Replace from here to the caret on accept. */
  slashStart: number;
  /** Text after the `/` and up to the caret (may include a `.field` portion). */
  query: string;
}

/**
 * Detect an in-progress `/topic` token at ``cursor`` for autocomplete.
 *
 * Walks back to the start of the current non-whitespace run, then takes the
 * first `/` in that run as the token start — but only when that `/` begins the
 * run or is preceded by a markdown wrapper character (backtick, quote, `*`,
 * bracket). This recognizes references typed inside `` `…` `` or `**…**` while
 * not firing on incidental slashes in words like ``src/utils``. Returns
 * ``null`` when there is no eligible `/` before the caret.
 */
export function activeTopicTokenAtCursor(
  text: string,
  cursor: number,
): ActiveTopicToken | null {
  let runStart = cursor;
  while (runStart > 0 && !/\s/.test(text[runStart - 1])) {
    runStart -= 1;
  }
  const slashStart = text.indexOf("/", runStart);
  if (slashStart === -1 || slashStart >= cursor) {
    return null;
  }
  const isEligible =
    slashStart === runStart || LEADING_WRAPPERS.test(text[slashStart - 1]);
  if (!isEligible) {
    return null;
  }
  return { slashStart, query: text.slice(slashStart + 1, cursor) };
}

/**
 * Split an active-token query into its topic portion and the field fragment
 * being typed, at the first `.`. Before any `.` the user is choosing a topic;
 * after it they are drilling into a field path.
 *
 * `"imu/da"` → `{ topicPart: "imu/da", fieldFragment: null }`
 * `"imu/data.lin"` → `{ topicPart: "imu/data", fieldFragment: "lin" }`
 */
export function splitTopicQuery(query: string): {
  topicPart: string;
  fieldFragment: string | null;
} {
  const dot = query.indexOf(".");
  if (dot === -1) {
    return { topicPart: query, fieldFragment: null };
  }
  return {
    topicPart: query.slice(0, dot),
    fieldFragment: query.slice(dot + 1),
  };
}

/** A resolved topic reference and the character spans it occupies. */
export interface TopicReferenceMatch {
  /** Index of the leading `/` of the matched topic name. */
  start: number;
  /** Exclusive end index of the topic name — where any `.field` suffix begins. */
  end: number;
  /**
   * Exclusive end index of the trailing `.field` path, or equal to ``end`` when
   * the reference has no field suffix. The span ``[end, fieldEnd)`` (which
   * includes the connecting `.`) is the field path written after the topic.
   */
  fieldEnd: number;
  /** The topic name the token resolved to (its portion before any `.field`). */
  name: string;
  /**
   * Whether ``name`` is a known org topic. Advisory only — it drives styling
   * (known vs not-yet-known), never whether the token counts as a reference.
   */
  known: boolean;
}

/**
 * Locate every topic reference in ``body``, with the character span of the
 * topic-name portion and, separately, of any trailing `.field` path.
 *
 * A candidate is recognized when its `/` sits at a token boundary (see
 * {@link startsAtTopicBoundary}) and it matches the topic grammar — so a slash
 * inside a word or file path (`logs/imu/data`) is never mistaken for a
 * reference, keeping extraction and highlighting in lockstep with autocomplete.
 * The name runs up to the first `.` (the field separator); the rest is the
 * field path. Recognition does not depend on ``knownTopicNames``: an author may
 * reference a topic that does not exist yet. The catalog only sets each match's
 * ``known`` flag, which callers use for styling. Used both to persist
 * ``relevant_topics`` (name only, known or not) and to highlight references —
 * topic name plus its field path — in the editor.
 */
export function findTopicReferences(
  body: string,
  knownTopicNames: readonly string[],
): TopicReferenceMatch[] {
  const known = new Set(knownTopicNames);
  const matches: TopicReferenceMatch[] = [];
  for (const match of body.matchAll(TOPIC_REFERENCE)) {
    if (match.index === undefined) {
      continue;
    }
    if (!startsAtTopicBoundary(body, match.index)) {
      continue;
    }
    // Trim a trailing separator dot the greedy field class may have grabbed
    // (e.g. the sentence-ending "." in "look at /imu/data.x.").
    const token = match[0].replace(TRAILING_NON_TOPIC, "");
    // The name runs up to the first `.`; anything past it is the field path.
    // The `.` is the grammatical separator, so no catalog is needed to split.
    const dot = token.indexOf(".");
    const name = dot === -1 ? token : token.slice(0, dot);
    matches.push({
      start: match.index,
      end: match.index + name.length,
      fieldEnd: match.index + token.length,
      name,
      known: known.has(name),
    });
  }
  return matches;
}

/**
 * Extract the distinct topic names referenced in ``body``, in first-appearance
 * order — including names not yet in ``knownTopicNames`` (authoring ahead of the
 * data is valid). See {@link findTopicReferences} for the recognition rules;
 * this is the deduped name-only projection persisted as ``relevant_topics``.
 */
export function extractTopicReferences(
  body: string,
  knownTopicNames: readonly string[],
): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const { name } of findTopicReferences(body, knownTopicNames)) {
    if (!seen.has(name)) {
      seen.add(name);
      result.push(name);
    }
  }
  return result;
}
